/**
 * Raster des pistes à la résolution du champ de hauteur (pas de 4 m), stocké
 * dans une texture RGBA lue par le shader du terrain :
 *   R = couverture (0..1), G = difficulté (0 verte .. 3 noire),
 *   B = manque de neige (0 = bien enneigée, 1 = sol visible), A = qualité.
 * Le tracé reste net quel que soit le LOD du maillage. La même grille sert
 * côté CPU à écarter les sapins et à sélectionner une piste au clic.
 */
import * as THREE from 'three/webgpu';
import { resample, type Heightfield, type Piste, type Vec2 } from '@snowchain/shared';

const DIFF_INDEX: Record<string, number> = { verte: 0, bleue: 1, rouge: 2, noire: 3 };

export class PisteRaster {
  readonly coverage: Float32Array;
  readonly index: Int32Array;
  readonly texture: THREE.DataTexture;
  private readonly data: Uint8Array;
  /** Grille du champ de hauteur : échantillons par côté, pas (m), demi-côté (m). */
  readonly res: number;
  readonly cell: number;
  readonly half: number;
  /** Pistes indexées (même ordre que `index`). */
  pistes: Piste[] = [];
  key = '';
  private conditionKey = '';

  constructor(hf: Heightfield) {
    this.res = hf.res;
    this.cell = hf.cell;
    this.half = hf.half;
    const n = hf.res * hf.res;
    this.coverage = new Float32Array(n);
    this.index = new Int32Array(n).fill(-1);
    this.data = new Uint8Array(n * 4);
    this.texture = new THREE.DataTexture(this.data, hf.res, hf.res, THREE.RGBAFormat);
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.generateMipmaps = false;
    this.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.needsUpdate = true;
  }

  /** Recalcule si les tracés ou l'état de la neige ont changé ; renvoie vrai si les tracés ont changé. */
  update(pistes: Iterable<Piste>): boolean {
    const list = [...pistes].sort((a, b) => a.id - b.id);
    const key = list.map((p) => `${p.id}:${p.path.length}:${p.width}:${p.difficulty}`).join('|');
    const conditionKey = list.map((p) => `${p.id}:${Math.round(p.snowDepth / 3)}:${Math.round(p.quality * 20)}`).join('|');
    this.pistes = list;
    const geometryChanged = key !== this.key;
    if (geometryChanged) {
      this.key = key;
      this.coverage.fill(0);
      this.index.fill(-1);
      list.forEach((p, idx) => this.rasterize(p, idx));
    }
    if (geometryChanged || conditionKey !== this.conditionKey) {
      this.conditionKey = conditionKey;
      this.writeTexture();
    }
    return geometryChanged;
  }

  private writeTexture(): void {
    const d = this.data;
    const thin = this.pistes.map((p) => Math.round(255 * Math.min(1, Math.max(0, (42 - p.snowDepth) / 26))));
    const quality = this.pistes.map((p) => Math.round(255 * Math.min(1, Math.max(0, p.quality))));
    const diff = this.pistes.map((p) => (DIFF_INDEX[p.difficulty] ?? 0) * 85);
    for (let k = 0; k < this.coverage.length; k++) {
      const idx = this.index[k] as number;
      const o = k * 4;
      if (idx < 0) {
        d[o] = 0;
        d[o + 1] = 0;
        d[o + 2] = 0;
        d[o + 3] = 255;
        continue;
      }
      d[o] = Math.round((this.coverage[k] as number) * 255);
      d[o + 1] = diff[idx] as number;
      d[o + 2] = thin[idx] as number;
      d[o + 3] = quality[idx] as number;
    }
    this.texture.needsUpdate = true;
  }

  private rasterize(p: Piste, idx: number): void {
    const { res, cell, half: mh } = this;
    const pts = resample(p.path, 8);
    const half = p.width / 2;
    const reach = half + 5;
    for (let s = 0; s < pts.length - 1; s++) {
      const a = pts[s] as Vec2;
      const b = pts[s + 1] as Vec2;
      const i0 = Math.max(0, Math.floor((Math.min(a[0], b[0]) - reach + mh) / cell));
      const i1 = Math.min(res - 1, Math.ceil((Math.max(a[0], b[0]) + reach + mh) / cell));
      const j0 = Math.max(0, Math.floor((Math.min(a[1], b[1]) - reach + mh) / cell));
      const j1 = Math.min(res - 1, Math.ceil((Math.max(a[1], b[1]) + reach + mh) / cell));
      const dx = b[0] - a[0];
      const dz = b[1] - a[1];
      const l2 = dx * dx + dz * dz || 1;
      for (let j = j0; j <= j1; j++) {
        const z = -mh + j * cell;
        for (let i = i0; i <= i1; i++) {
          const x = -mh + i * cell;
          let t = ((x - a[0]) * dx + (z - a[1]) * dz) / l2;
          t = t < 0 ? 0 : t > 1 ? 1 : t;
          const dist = Math.hypot(x - (a[0] + dx * t), z - (a[1] + dz * t));
          if (dist > reach) continue;
          const e = (reach - dist) / (reach - half + 2);
          const c = e >= 1 ? 1 : e * e * (3 - 2 * e);
          const k = j * res + i;
          if (c > (this.coverage[k] as number)) {
            this.coverage[k] = c;
            this.index[k] = idx;
          }
        }
      }
    }
  }

  /** Couverture au plus proche échantillon. */
  sample(x: number, z: number): { cov: number; piste: Piste | null } {
    const { res, cell, half } = this;
    const i = Math.round((x + half) / cell);
    const j = Math.round((z + half) / cell);
    if (i < 0 || j < 0 || i >= res || j >= res) return { cov: 0, piste: null };
    const k = j * res + i;
    const idx = this.index[k] as number;
    return { cov: this.coverage[k] as number, piste: idx >= 0 ? (this.pistes[idx] ?? null) : null };
  }
}
